import { BaseDialog } from "@app/components/BaseDialog";
import { usePageTree } from "@app/hooks/usePageTree";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { CarTreeItem } from "@app/components/TreeSelect";
import { Fragment, ReactElement } from "react";
import { Box, Typography } from "@mui/material";
import { CarIconCheck, CarIconRemove } from "@app/components/Icons";
import {
  useCommonCmpasModuleListQuery,
  useCommonCmpasModuleSectionListQuery,
  useCommonRolesRetrieveQuery,
} from "@app/api/carApi.generated";
import { useLocalStorage } from "@app/hooks/useLocalStorage";
import { CarCheckbox } from "@app/components/Checkbox";

interface RolePermissionsDialogProps {
  id: string;
  label: string;
  onApply: (value: boolean) => void;
  onCancel: () => void;
}

export const RolePermissionsDialog = (props: RolePermissionsDialogProps) => {
  const [showOnlyChecked, setShowOnlyChecked] = useLocalStorage(
    "RolePermissionsDialog_showOnlyChecked",
    false,
  );

  const role = useCommonRolesRetrieveQuery({
    id: props.id,
  });
  const modules = useCommonCmpasModuleListQuery();
  const pages = useCommonCmpasModuleSectionListQuery();
  const pagesTree = usePageTree();

  const moduleIds = role.data?.module_access ?? [];
  const checkedModules: string[] =
    modules.data
      ?.filter((m) => m.is_active && moduleIds.some((mid) => mid === m.id))
      .map((m) => m.id ?? "") ?? [];

  const checkedItems =
    pages.data
      ?.filter(
        (i) =>
          i.is_active && checkedModules.some((cm) => i.cmpas_module === cm),
      )
      .map((i) => i.id ?? "") ?? [];

  const isSomeChildrenChecked = (item: CarTreeItem) => {
    return item.children.length === 0
      ? checkedItems.some((ch) => ch === item.id)
      : item.children.some(isSomeChildrenChecked);
  };

  const renderItem = (item: CarTreeItem, level: number): ReactElement => {
    if (showOnlyChecked && !isSomeChildrenChecked(item)) {
      return <Fragment key={item.id} />;
    }
    return (
      <Fragment key={item.id}>
        {
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: 34,
              ml: level * 4,
            }}
          >
            {isSomeChildrenChecked(item) ? (
              <CarIconCheck
                sx={{
                  color: "green",
                  m: 1,
                }}
              />
            ) : (
              <CarIconRemove
                sx={{
                  color: "red",
                  m: 1,
                }}
              />
            )}
            <Typography
              key={item.id}
              variant="par01SemiBold"
              sx={{ color: "black" }}
            >
              {item.label}
            </Typography>
          </Box>
        }
        {item.children.map((i) => renderItem(i, level + 1))}
      </Fragment>
    );
  };

  return (
    <BaseDialog
      sx={{ minWidth: 800, maxWidth: "100vw" }}
      onClose={props.onCancel}
    >
      <Typography variant="h2SSemiBold">
        {`Permissions - ${props.label}`}
      </Typography>
      <TablePlaceHolder
        isLoading={
          pagesTree.isLoading ||
          role.isLoading ||
          modules.isLoading ||
          pages.isLoading
        }
        rows={8}
      >
        <CarCheckbox
          label="Show enabled only"
          checked={showOnlyChecked}
          onChange={(e, checked) => setShowOnlyChecked(checked)}
        />
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            mr: -1,
            pr: 1,
          }}
        >
          {pagesTree.items.map((i) => renderItem(i, 0))}
        </Box>
      </TablePlaceHolder>
    </BaseDialog>
  );
};
